import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  APP_INITIALIZER,
  signal,
  computed,
} from '@angular/core';
import { provideRouter, TitleStrategy } from '@angular/router';
import { PortalTitleStrategy } from './title-strategy';
import { provideHttpClient, withInterceptorsFromDi, HttpClient } from '@angular/common/http';

import { routes } from './app.routes';

// ─── Connection meta signals ──────────────────────────────────────────────────
// Populated once by the APP_INITIALIZER CSRF call; shared across all login
// pages without a second network round-trip.

export const connectionIp = signal<string | null>(null);

export interface ConnectionTls {
  isHttps:  boolean;
  /** e.g. "TLSv1.3" — null when running over plain HTTP */
  protocol: string | null;
  /** e.g. "TLS_AES_256_GCM_SHA384" — null when running over plain HTTP */
  cipher:   string | null;
}

export const connectionTls = signal<ConnectionTls | null>(null);

/**
 * Human-readable TLS badge label.
 *
 * - null          → still loading (show skeleton)
 * - "HTTP · Not encrypted" → development / plain HTTP
 * - "TLS 1.3 · AES-256-GCM" (or real values) → production HTTPS
 */
export const connectionTlsLabel = computed((): string | null => {
  const t = connectionTls();
  if (t === null) return null; // loading

  if (!t.isHttps) return 'HTTP · Not encrypted';

  if (t.protocol && t.cipher) {
    // "TLSv1.3"                → "TLS 1.3"
    const proto = t.protocol.replace(/^TLSv?(\d)/, 'TLS $1');

    // "TLS_AES_256_GCM_SHA384" → "AES-256-GCM"
    const cipher = t.cipher
      .replace(/^TLS_/, '')
      .replace(/_SHA\d+$/, '')
      .replace(/_/g, '-');

    return `${proto} · ${cipher}`;
  }

  return 'HTTPS · Encrypted';
});

// ─── App initialiser ──────────────────────────────────────────────────────────

interface CsrfResponse {
  csrf:     string;
  ip:       string;
  tls: {
    protocol: string | null;
    cipher:   string | null;
  };
}

function initCsrf(http: HttpClient): () => Promise<void> {
  return () =>
    http
      .get<CsrfResponse>('/api/csrf')
      .toPromise()
      .then(res => {
        if (res?.ip) connectionIp.set(res.ip);

        connectionTls.set({
          isHttps:  window.location.protocol === 'https:',
          protocol: res?.tls?.protocol ?? null,
          cipher:   res?.tls?.cipher   ?? null,
        });
      })
      .catch(() => {
        connectionIp.set('unavailable');
        connectionTls.set({
          isHttps:  window.location.protocol === 'https:',
          protocol: null,
          cipher:   null,
        });
      });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    { provide: TitleStrategy, useClass: PortalTitleStrategy },
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide:    APP_INITIALIZER,
      useFactory: initCsrf,
      deps:       [HttpClient],
      multi:      true,
    },
  ],
};
